/**
 * Light background tints for review pages, keyed by customer provider_id.
 * Distinct hues and moderate saturation so each customer is easy to tell apart;
 * lightness kept high (95–96%) so text contrast remains good.
 * RFT is intentionally darker (83%) to stand out as orange/red.
 */
const REVIEW_PAGE_BACKGROUNDS: Record<string, string> = {
  TEST: "hsl(280 45% 95%)", // violet
  ELHT: "hsl(220 50% 95%)", // blue
  UCLH: "hsl(150 45% 95%)", // green
  BHT: "hsl(35 55% 95%)", // amber
  ASP: "hsl(330 50% 95%)", // pink/magenta
  UHCW: "hsl(162 38% 96%)", // soft sage
  RFT: "hsl(15 85% 83%)", // orange/red — intentionally stronger
  LNW: "hsl(190 45% 95%)", // teal
  THH: "hsl(255 45% 95%)", // indigo
};

const FALLBACK_BACKGROUND = "hsl(0 0% 98%)";

/** Stronger tint (same hue/saturation, lower lightness) for accents e.g. Trust Rules button. */
const TRUST_RULES_BUTTON_LIGHTNESS = 85;

/** Darker variant for Trust Rules button when in the app header bar (for white text contrast). */
const TRUST_RULES_BUTTON_HEADER_LIGHTNESS = 38;

/**
 * Returns a stronger background colour for the Trust Rules button (and similar accents)
 * using the same hue as the review page but lower lightness so it reads as the same customer colour.
 */
export function getTrustRulesButtonBackground(providerId: string | null): string {
  if (!providerId) return FALLBACK_BACKGROUND;
  const pageColour = REVIEW_PAGE_BACKGROUNDS[providerId];
  if (!pageColour) return FALLBACK_BACKGROUND;
  const match = pageColour.match(/^hsl\((\d+)\s+(\d+)%\s+(\d+)%\)$/);
  if (!match) return FALLBACK_BACKGROUND;
  const [, h, s] = match;
  return `hsl(${h} ${s}% ${TRUST_RULES_BUTTON_LIGHTNESS}%)`;
}

/**
 * Returns a darker background for the Trust Rules button when placed in the app header bar,
 * so white text has sufficient contrast.
 */
export function getTrustRulesButtonBackgroundHeader(providerId: string | null): string {
  if (!providerId) return "hsl(220 50% 38%)";
  const pageColour = REVIEW_PAGE_BACKGROUNDS[providerId];
  if (!pageColour) return "hsl(220 50% 38%)";
  const match = pageColour.match(/^hsl\((\d+)\s+(\d+)%\s+(\d+)%\)$/);
  if (!match) return "hsl(220 50% 38%)";
  const [, h, s] = match;
  return `hsl(${h} ${s}% ${TRUST_RULES_BUTTON_HEADER_LIGHTNESS}%)`;
}

/**
 * Returns a subtle background colour for the review page based on customer.
 *
 * @param providerId - Customer provider_id (e.g. "ELHT", "RFT"), or null
 * @returns CSS colour value (e.g. hsl(...)) or fallback neutral for unknown
 */
export function getReviewPageBackground(providerId: string | null): string {
  if (!providerId) return FALLBACK_BACKGROUND;
  return REVIEW_PAGE_BACKGROUNDS[providerId] ?? FALLBACK_BACKGROUND;
}
